import { Injectable } from '@angular/core';
import { UserActionItem } from 'app/modules/today/models/userActionItem.model';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import {
  ActionItemTaskCompleted, CheckNewLeadCallTask,
  CompleteTask,
  CreateActionItemTaskComment, DispatchBorrowerContextActions,
  GetUserActionItemDetails, SetUserActionItemDetails,
  UnsetActiveUserActionItem, UpdateDocument, ProcessDocument, UpdateAndProcessDocument
} from 'ng2-app/modules/today/state/user-action-items.action';
import { UserActionItemsService } from 'ng2-app/modules/today/services/userActionItems.service';
import { TaskService } from 'lendio-borrower/models/task/task.service';
import { patch } from '@ngxs/store/operators';
import { catchError, of, tap } from 'rxjs';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';
import { ClearBorrowerStore, GetBorrower } from 'ng2-app/store/borrower/borrower/borrower.actions';
import { GetBorrowerDocuments } from 'ng2-app/store/documents/document.actions';
import { GetBankAccountValues } from 'ng2-app/store/bank-account/bank-account.actions';
import { ClearContactStore } from 'ng2-app/store/contact/contact.actions';
import { GetBorrowerValues } from 'ng2-app/store/borrower-value/borrower-value.actions';
import { DocumentsService } from 'ng2-app/services/documents/documents.service';
import { GetTodayBadgeCounts } from './today-badge-counts/today-badge-counts.action';
import { ClearNonOwnerPersonnelStore } from 'ng2-app/store/non-owner-personnel/non-owner-personnel.actions';
import { OcrolusService } from 'app/services/documents/ocrolus.service';
import { ClearFundingNeedsStore, GetFundingNeeds } from 'ng2-app/store/funding-needs/funding-needs.actions';

export class UserActionItemDetailStateModel {
  userId: null | number;
  borrowerId: null | number;
  loading: boolean;
  commentSaving: boolean;
  documentSaving: boolean;
  activeActionItem: UserActionItem | any;
  snapshotState?: any;
}

@State<UserActionItemDetailStateModel>({
  name: 'UserActionItemDetail',
  defaults: {
    borrowerId: null,
    userId: null,
    loading: false,
    commentSaving: false,
    documentSaving: false,
    activeActionItem: null,
  },
})
@Injectable()
export class UserActionItemDetailState {

  @Selector()
  static activeActionItem(state: UserActionItemDetailStateModel) {
    return state.activeActionItem;
  }

  @Selector()
  static borrowerId(state: UserActionItemDetailStateModel) {
    return state.borrowerId;
  }

  @Selector()
  static commentSaving(state: UserActionItemDetailStateModel) {
    return state.commentSaving;
  }

  @Selector()
  static loading(state: UserActionItemDetailStateModel) {
    return state.loading;
  }

  @Selector()
  static documentSaving(state: UserActionItemDetailStateModel) {
    return state.documentSaving;
  }

  constructor(
    private actionItemsService: UserActionItemsService,
    private documentsService: DocumentsService,
    private eventBusService: EventBusService,
    private store: Store,
    private taskService: TaskService,
    private ocrolusService: OcrolusService
  ) {}

  @Action(UnsetActiveUserActionItem)
  unsetActiveUserActionItem(
    { patchState }: StateContext<UserActionItemDetailStateModel>,
  ) {
    patchState({
      borrowerId: null,
      activeActionItem: null,
    });
  }

  @Action(GetUserActionItemDetails)
  getActionItemDetails(
    { getState, patchState }: StateContext<UserActionItemDetailStateModel>,
    { actionItemId }: GetUserActionItemDetails
  ){
    patchState({
      loading: true,
    });

    const currentBorrowerId = getState().borrowerId;

    this.actionItemsService.getUserActionItemDetails(actionItemId)
      .pipe(
        tap((response) => {
          if (typeof response !== 'string' && response.borrowerId !== undefined) {
            const newBorrowerId = response.borrowerId;
            patchState({
              activeActionItem: response,
              loading: false,
              borrowerId: newBorrowerId
            });

            if (newBorrowerId && (newBorrowerId !== currentBorrowerId)) {
              this.store.dispatch(new DispatchBorrowerContextActions(newBorrowerId));
            }
          } else {
            console.error('Invalid response type or missing borrowerId:', response);
          }
        }),
        catchError(error => {
          console.error(error);
          return of();
        })
      ).subscribe();
  }

  @Action( CheckNewLeadCallTask )
  checkNewLeadCallTask(
    { getState }: StateContext<UserActionItemDetailStateModel>,
    { lenderUserId, borrowerId }: CheckNewLeadCallTask,
  ) {
    const currentItem = getState().activeActionItem;
    if( currentItem?.typeId === 12 && currentItem?.borrowerId === borrowerId ) {
      this.store.dispatch( new ActionItemTaskCompleted(currentItem.modelId) );
      this.store.dispatch( new GetTodayBadgeCounts(lenderUserId) );
    }
  }

  @Action(SetUserActionItemDetails)
  setUserActionItemDetails(
    { getState, patchState, setState }: StateContext<UserActionItemDetailStateModel>,
    { actionItemDetails }: SetUserActionItemDetails
  ) {
    const snapshotState = getState();
    patchState({
      activeActionItem: actionItemDetails,
      snapshotState,
    });
  }

  @Action(CreateActionItemTaskComment)
  createActionItemTaskComment(
    { getState, setState, patchState }: StateContext<UserActionItemDetailStateModel>,
    { commentModel, actionItem }: CreateActionItemTaskComment
  ) {
    patchState({
      commentSaving: true
    });
    return this.taskService.createNewComment(commentModel.description || '', actionItem.modelId, commentModel.isForLender)
      .pipe(
        tap( newComment => {
          newComment.authorName = `${newComment.creatorUser.first} ${newComment.creatorUser.last}`;
          const currentActiveItem = getState().activeActionItem;
          if(currentActiveItem.id === actionItem.id) {
            const details = currentActiveItem.details;
            details.additionalDetails.comments = details.additionalDetails.comments.concat(newComment);
            setState(
              patch({
                activeActionItem: patch({
                  details
                }),
                commentSaving: false,
              })
            );
            this.eventBusService.publish( new this.eventBusService.types.TaskCommentCreatedEvent() );
          }
        }),
        catchError( error => {
          console.error(error);
          setState(patch({ commentSaving: false }));
          return of('');
        })
      );
  }

  @Action(CompleteTask)
  completeTask(
    { patchState }: StateContext<UserActionItemDetailStateModel>,
    {taskId, lenderUserId}: CompleteTask
  ) {
    this.actionItemsService.completeTaskById(taskId, lenderUserId)
      .pipe(
        tap((response) => {
          const borrowerKickback = {
            borrowerId: response.borrowerId,
            borrowerName: response['borrower']['name'] ?? 'this borrower',
            kickback: response.type?.toLowerCase() === 'kickback' ? 'kickback' : null
          };
          patchState({ activeActionItem: null });

          this.store.dispatch( new ActionItemTaskCompleted(taskId, borrowerKickback) );
          this.store.dispatch( new GetTodayBadgeCounts(lenderUserId) );
        }),
        catchError( error => {
          console.error(error);
          return of('');
        })
      ).subscribe();
  }

  @Action(UpdateDocument)
  updateDocument(
    { patchState }: StateContext<UserActionItemDetailStateModel>,
    { document, userId }: UpdateDocument
  ) {
    patchState({ documentSaving: true });
    return this.documentsService.update(document).pipe(
      catchError(() => {
          this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(false,'Error saving Document.') );
          patchState({documentSaving: false});
          return of();
        }
      ),
      tap(() => {
        this.store.dispatch( new UnsetActiveUserActionItem() );
        this.store.dispatch( new ActionItemTaskCompleted(document.id) );
        this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(true,'Document saved.') );
        patchState({documentSaving: false});
        this.store.dispatch( new GetTodayBadgeCounts(userId) );
      })
    );
  }

  @Action(UpdateAndProcessDocument)
  updateAndProcessDocument(
    { patchState }: StateContext<UserActionItemDetailStateModel>,
    { document, userId, borrowerId }: UpdateAndProcessDocument
  ) {
    if (document.scanStatus !== 'CLEAN') {
      this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(false,'Document is not Clean') );
      return;
    }

    patchState({ documentSaving: true });
    return this.documentsService.update(document).pipe(
      catchError(() => {
          this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(false,'Error saving Document.') );
          patchState({documentSaving: false});
          return of();
        }
      ),
      tap(() => {
        this.store.dispatch( new ProcessDocument(document, userId, borrowerId));
      })
    );
  }

  @Action(ProcessDocument)
  processDocument(
    { patchState }: StateContext<UserActionItemDetailStateModel>,
    { document, userId, borrowerId }: ProcessDocument
  ) {
    patchState({ documentSaving: true });
    if (document.scanStatus !== 'CLEAN') {
      this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(false,'Document is not Clean') );
      patchState({documentSaving: false});
      return;
    }

    return this.ocrolusService.sendToOcrolus(borrowerId, [document.id], true).pipe(
      catchError((error) => {
        console.error(error);
        this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(false,'Error processing document') );
        patchState({documentSaving: false});
        return of();
      }),
      tap(() => {
        this.store.dispatch( new UnsetActiveUserActionItem() );
        this.store.dispatch( new ActionItemTaskCompleted(document.id) );
        this.eventBusService.publish( new this.eventBusService.types.DocumentSaveEvent(true,'Document sent for processing') );
        patchState({documentSaving: false});
        this.store.dispatch( new GetTodayBadgeCounts(userId) );
        return of();
      })
    );
  }

  @Action(DispatchBorrowerContextActions)
  dispatchBorrowerContextActions(
    {}: StateContext<UserActionItemDetailStateModel>,
    {borrowerId}: DispatchBorrowerContextActions
  ) {
    [
      new ClearBorrowerStore(),
      new ClearNonOwnerPersonnelStore(),
      new ClearContactStore(),
      new ClearFundingNeedsStore(),
      new GetBorrower(borrowerId, true, false),
      new GetBorrowerDocuments(borrowerId),
      new GetBankAccountValues(borrowerId),
      new GetBorrowerValues(borrowerId),
      new GetFundingNeeds(borrowerId),
    ].forEach( action => this.store.dispatch(action) );
  }
}
